import { percentOf } from '../../platform/percentOf'
import { isEffectivelyDisconnected } from './accountHealth'
import { groupOf, isLiability, type Group } from './accountGroups'
import type { Account } from './useAccounts'

/**
 * The single sign flip in this module. **A liability is POSITIVE on the
 * wire** — `plaid_mapper.py:89` stores Plaid's `balances.current` unflipped,
 * and Plaid reports a card's `current` as the amount OWED. The design's
 * `−$2,318.44` is presentation, applied here once rather than as an inline
 * ternary per component (M5 spec, D-M5-4).
 */
export function signedCents(rawCents: number, group: Group): number {
  return isLiability(group) ? -rawCents : rawCents
}

/**
 * The ONE exclusion rule for balance totals (Q4), mirroring the backend's
 * `counts_toward_balance_totals`: a disconnected account's balance is stale
 * money the bank no longer reports, so it is in no net worth, asset,
 * liability or group total. Its TRANSACTIONS still count in spending —
 * nothing here touches those.
 *
 * Out: `isEffectivelyDisconnected` (accountHealth.ts) — `disconnected`, and a
 * non-manual account with a null status (a dangling row). A manual account
 * (null status by design) counts. **This deliberately differs from the backend
 * today, which excludes only `'disconnected'`** and so still sums a dangling
 * row; the backend is being asked to align. Until then the client follows what
 * the row displays, because showing "Disconnected" beside a counted balance
 * contradicts itself. `reconnectable` is irrelevant: it says whether Reconnect
 * is offered, not whether the money is current.
 */
export function countsTowardTotals(a: Account): boolean {
  return !isEffectivelyDisconnected(a)
}

/** Accounts that count AND have a balance — the one input to `totalsOf`,
 *  `currenciesOf` and every `hasBalance`, so no caller can filter a different
 *  way. An account row still shows its own balance; this rule is for totals. */
export function balancesForTotals(accounts: Account[]): (Account & { currentCents: number })[] {
  return accounts.filter(
    (a): a is Account & { currentCents: number } => a.currentCents !== null && countsTowardTotals(a),
  )
}

export type Totals = {
  assetsCents: number
  liabilitiesCents: number
  netWorthCents: number
  /** The single currency every included balance shares — `totalsOf` already
   *  proved the set has size 1 (or 0) to get here, so the pairing travels
   *  with the totals rather than being re-derived, and possibly defaulted
   *  wrong, at the call site (issue #395, B2). `null` only when there is no
   *  balance to report at all; the MIXED-currency case returns `null` for
   *  the whole object instead, one level up. */
  currency: string | null
}

/**
 * The three totals across the top of the list — asserted against the shared
 * fixture, never recomputed independently of it (the fixture README: "a
 * client that sums the rows itself is testing its own arithmetic, not the
 * backend").
 *
 * `null` when more than one currency appears among the accounts that HAVE a
 * balance AND count toward totals (`balancesForTotals`: a disconnected account
 * is in no total) (M5-R8/D21) — there is no FX endpoint, so a single number would be
 * a lie. An account with a `null` balance is skipped rather than treated as
 * zero, and contributes no currency to that check either.
 *
 * `liabilitiesCents` stays POSITIVE in the return value. The sign flip is for
 * rendering a ROW; a tile labelled "Total liabilities" showing a negative
 * number would be wrong twice. Routed through `signedCents` and negated back
 * rather than reading the raw balance directly — the deliberate choice is
 * that a broken sign flip breaks THIS total too, not only a row's display.
 */
export function totalsOf(accounts: Account[]): Totals | null {
  const withBalance = balancesForTotals(accounts)

  const currencies = new Set(withBalance.map((a) => a.currency))
  if (currencies.size > 1) return null

  let assetsCents = 0
  let liabilitiesCents = 0
  for (const account of withBalance) {
    const group = groupOf(account.type)
    const signed = signedCents(account.currentCents, group)
    if (isLiability(group)) liabilitiesCents += -signed
    else assetsCents += signed
  }

  const currency = withBalance[0]?.currency ?? null
  return { assetsCents, liabilitiesCents, netWorthCents: assetsCents - liabilitiesCents, currency }
}

/**
 * Credit utilisation — M5-R6, D-M5-7. `limit = current + available`, because
 * for a Plaid credit account `available_balance` IS the remaining credit.
 * Fixture: 128745 + 871255 = exactly $10,000. No `credit_limit` column exists
 * on the wire and none is planned (backend confirmed 2026-09-11), so this is
 * derived rather than read.
 *
 * `null` unless the account is a credit account, both balances are non-null
 * and the derived limit is positive. Plaid returns `available: null` for some
 * institutions, and deriving a limit from a null balance yields limit ===
 * balance — a confident, wrong 100% utilised. A wrong number is worse than an
 * absent one, so any missing input hides the meter rather than guessing.
 */
export function utilisation(a: Account): { pct: number; limitCents: number } | null {
  if (groupOf(a.type) !== 'credit') return null
  if (a.currentCents === null || a.availableCents === null) return null

  const limitCents = a.currentCents + a.availableCents
  if (limitCents <= 0) return null

  return { pct: percentOf(a.currentCents, limitCents), limitCents }
}
